/** Public event URLs: /events/:organizerSlug/:eventSlug */

import type { Location } from 'react-router-dom';

export type EventPathInput = {
  slug: string;
  organizer_username?: string | null;
};

export function eventOrganizerSlugSegment(username: string): string {
  const segment = sanitizeEventSlugInput(username);
  return segment && segment !== 'event' ? segment : 'organizer';
}

export function eventOrganizerSlugPrefix(username: string): string {
  return `${eventOrganizerSlugSegment(username)}-`;
}

export function eventSlugSuffixForOrganizer(organizerUsername: string, fullSlug: string): string {
  const prefix = eventOrganizerSlugPrefix(organizerUsername);
  const normalized = sanitizeEventSlugInput(fullSlug);
  if (normalized.startsWith(prefix)) {
    return normalized.slice(prefix.length);
  }
  return normalized;
}

export function eventFullSlugWithOrganizer(organizerUsername: string, suffix: string): string {
  const prefix = eventOrganizerSlugPrefix(organizerUsername);
  let part = sanitizeEventSlugInput(suffix);
  if (!part) part = 'event';
  if (part.startsWith(prefix)) {
    return part.slice(0, 150);
  }
  return sanitizeEventSlugInput(`${prefix}${part}`).slice(0, 150);
}

export function sanitizeEventSlugTyping(raw: string): string {
  return raw
    .toLowerCase()
    .replace(/[\s_]+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-+/, '');
}

/** Final normalize for submit/API (mirrors backend eventNormalizeSlugInput). */
export function sanitizeEventSlugInput(raw: string): string {
  return sanitizeEventSlugTyping(raw).replace(/-+$/g, '');
}

export function validateEventSlug(slug: string): string | null {
  if (!slug) return 'Event URL cannot be empty';
  if (slug.length < 2) return 'Event URL must be at least 2 characters';
  if (slug.length > 150) return 'Event URL must be 150 characters or fewer';
  if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug)) {
    return 'Event URL can only use lowercase letters, numbers, and hyphens';
  }
  return null;
}

export function eventPublicPath(input: EventPathInput | string): string {
  if (typeof input === 'string') {
    return `/events/${encodeURIComponent(input.trim())}`;
  }
  const { slug, organizer_username: organizerUsername } = input;
  if (organizerUsername) {
    const org = eventOrganizerSlugSegment(organizerUsername);
    const suffix = eventSlugSuffixForOrganizer(organizerUsername, slug);
    return `/events/${encodeURIComponent(org)}/${encodeURIComponent(suffix)}`;
  }
  return `/events/${encodeURIComponent(slug.trim())}`;
}

export function eventPublicSubPath(input: EventPathInput | string, subPath: string): string {
  const base = eventPublicPath(input).replace(/\/$/, '');
  const sub = subPath.replace(/^\//, '');
  return `${base}/${sub}`;
}

export type EventRouteParams = {
  organizerSlug?: string;
  eventSlug?: string;
  slug?: string;
};

export type EventSlugQuery =
  | { kind: 'legacy'; slug: string }
  | { kind: 'nested'; organizer: string; slug: string };

export function eventSlugQueryFromParams(params: EventRouteParams): EventSlugQuery | null {
  if (params.organizerSlug && params.eventSlug) {
    return { kind: 'nested', organizer: params.organizerSlug, slug: params.eventSlug };
  }
  if (params.slug) {
    return { kind: 'legacy', slug: params.slug };
  }
  return null;
}

export function eventApiQueryFromSlugQuery(
  query: EventSlugQuery | null
): string | { organizer: string; slug: string } | { legacySlug: string } | null {
  if (!query) return null;
  if (query.kind === 'nested') {
    return { organizer: query.organizer, slug: query.slug };
  }
  return { legacySlug: query.slug };
}

export function eventSlugQueryFromLocation(location: Location): EventSlugQuery | null {
  const match = location.pathname.match(/^\/events\/([^/]+)(?:\/([^/]+))?/);
  if (!match) return null;
  const [, first, second] = match;
  if (second) {
    return { kind: 'nested', organizer: decodeURIComponent(first), slug: decodeURIComponent(second) };
  }
  return { kind: 'legacy', slug: decodeURIComponent(first) };
}

export function eventBasePathFromLocation(pathname: string): string {
  return pathname
    .replace(/\/payment\/return$/, '')
    .replace(/\/tickets\/return$/, '')
    .replace(/\/register$/, '')
    .replace(/\/tickets$/, '')
    .replace(/\/archive$/, '');
}
